import React, { useRef, useState } from 'react';
import Typography from '@mui/material/Typography';
import { Box, TextField, IconButton } from '@mui/material';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import Popper from '@mui/material/Popper';
import PropTypes from 'prop-types';

/**
 * Reusable form field component that combines a label and RenderField
 */
const RenderField = ({ children, select, value, name, readOnly, onChange, onBlur, label = 'Field', error = '' }) => {
  if (readOnly) return <div className="readonly-field-component">{value || children || 'n/a'}</div>;

  const helperText = error.replace('$fieldName', label);

  return (
    <TextField
      select={select}
      className="field-component"
      value={value || ''}
      onChange={onChange}
      onBlur={onBlur}
      name={name}
      error={!!error}
      helperText={helperText}
    >
      {children}
    </TextField>
  );
};

const FormField = ({
  label,
  name,
  value,
  onChange,
  onBlur,
  readOnly,
  error,
  select = false,
  required = false,
  children,
  tooltip,
}) => {
  const tooltipRef = useRef();
  const [popperOpen, setPopperOpen] = useState(false);

  const handleMouseEnter = () => {
    setPopperOpen(true);
  };

  const handleMouseLeave = () => {
    setPopperOpen(false);
  };

  const labelContent = (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Typography variant="body1" className="field-label">
        {label}
        {required && '*'}
      </Typography>
      {tooltip && (
        <>
          <IconButton
            ref={tooltipRef}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            size="small"
            sx={{ ml: 'auto', marginLeft: '-20px', p: 0.25 }}
          >
            <InfoOutlined sx={{ color: '#00395d', fontSize: 16 }} />
          </IconButton>
          <Popper placement="top" open={popperOpen} anchorEl={tooltipRef.current} style={{ zIndex: 9999999 }}>
            <Box
              sx={{
                border: 1,
                borderColor: '#004',
                borderRadius: 1,
                p: 1,
                bgcolor: 'white',
                color: '#66f',
                boxShadow: 12,
                fontWeight: 'bold',
                maxWidth: 300,
              }}
            >
              {tooltip}
            </Box>
          </Popper>
        </>
      )}
    </Box>
  );

  return (
    <Box className="field">
      {/* Label */}
      {labelContent}

      {/* Input Field */}
      <RenderField
        name={name}
        onChange={onChange}
        onBlur={onBlur}
        value={value || ''}
        readOnly={readOnly}
        error={error}
        label={label}
        select={select}
        className="field-component"
      >
        {children}
      </RenderField>
    </Box>
  );
};

FormField.propTypes = {
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  value: PropTypes.any,
  onChange: PropTypes.func,
  readOnly: PropTypes.bool,
  error: PropTypes.string,
  select: PropTypes.bool,
  required: PropTypes.bool,
  children: PropTypes.node,
  tooltip: PropTypes.string,
};

export default FormField;
